import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { PageTitle } from 'ui/PageTitle';
import { FilterChipGroup, IFilterChipOption } from 'ui/FilterChip';
import { TextInput } from 'ui/TextInput';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { SvgIcon } from 'ui/SvgIcon';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useExploreHotelsList, useHotelAdminOptions, useDestinationOptions } from './hooks/useHotelCollectionQueries';
import { useHotelCollectionUrlFilters } from './hooks/useHotelCollectionUrlFilters';
import { useCurrentUserAssignedDestinationsQuery } from '../HotelContainer/useHotelContainerQueries';
import StarIcon from 'ui/Icons/star.component.svg';
import MagnifyingGlassIcon from 'ui/Icons/magnifying-glass.component.svg';
import FluidButton from 'ui/FluidButton';
import { FiltersDrawer } from './FiltersDrawer';

export interface IHotelCollectionListProps {}

/**
 * Filter state for hotel collection list
 * Field names match API request structure for easier mapping
 */
export interface IHotelCollectionFilters {
  name: string; // Hotel name search (maps to API 'name' field)
  countryCodes: string[]; // Array of 2-letter country codes (e.g., ["MV", "SC"])
  bestFors: string[]; // Array of filter values from EnumHotelFilters
}

/**
 * Format location string from region (or country as fallback)
 * If region is null, display country name instead
 */
const formatLocation = (region: string | null, countryName: string): string => {
  if (region) {
    return region.toUpperCase();
  }
  return countryName.toUpperCase();
};

/**
 * Format star rating with "STAR" suffix
 * Returns null if starRating is null (don't render)
 */
const formatStarRating = (starRating: string | null): string | null => {
  if (!starRating) return null;
  // If already includes "STAR", return as-is, otherwise append " STAR"
  const upperRating = starRating.toUpperCase();
  return upperRating.includes('STAR') ? upperRating : `${starRating} STAR`;
};

export const HotelCollectionList: React.FC<IHotelCollectionListProps> = () => {
  const queryClient = useQueryClient();

  // Filter state - synced to URL query parameters
  const {
    name,
    debouncedName,
    countryCodes,
    bestFors,
    setName,
    setCountryCodes,
    setBestFors,
    applyFilters,
    currentFilters,
  } = useHotelCollectionUrlFilters();

  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);

  // SR destination restriction: fetch assigned destinations for current SR user
  const isSr = useSelector(AuthSelectors.isSr);
  const currentUserUuid = useSelector(AuthSelectors.getCurrentUserUuid) as string | null;
  const { data: srAssignedDestinations, isLoading: isLoadingSrDestinations } =
    useCurrentUserAssignedDestinationsQuery(currentUserUuid, isSr);

  // Cleanup queries on unmount
  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: ['hotelCollection'] });
    };
  }, [queryClient]);

  // Fetch destination options from API (same source as Enquiries page for consistency)
  const { data: destinationCountries, isPending: destinationsPending, isError: destinationsError } = useDestinationOptions();
  const destinationOptions: IFilterChipOption[] = useMemo(
    () =>
      (destinationCountries || [])
        .map(country => ({
          value: country.code, // 2-letter code (e.g., "MV")
          label: country.name, // Full name (e.g., "Maldives")
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [destinationCountries]
  );

  // SR destination restriction: filter destination options to only show assigned destinations
  const filteredDestinationOptions = useMemo(() => {
    if (!srAssignedDestinations || srAssignedDestinations.length === 0) {
      return destinationOptions;
    }
    return destinationOptions.filter(opt => srAssignedDestinations.includes(opt.value));
  }, [destinationOptions, srAssignedDestinations]);

  // SR destination restriction: when SR has assigned destinations and no explicit filter,
  // auto-restrict results to assigned destinations
  const effectiveCountryCodes = useMemo(() => {
    if (!srAssignedDestinations || srAssignedDestinations.length === 0) {
      return countryCodes;
    }
    return countryCodes.length > 0 ? countryCodes : srAssignedDestinations;
  }, [countryCodes, srAssignedDestinations]);

  // Fetch best-for filter options from API
  const { data: optionsData, isPending: optionsPending, isError: optionsError } = useHotelAdminOptions();
  const bestForOptions: IFilterChipOption[] = useMemo(() => {
    if (!optionsData) return [];

    const bestForCategory = optionsData.data.filtersCategories.find(cat => cat.categoryKey === 'BEST_FOR');

    return (
      bestForCategory?.filters.map(filter => ({
        value: filter.filterEnumName,
        label: filter.filterTitle,
      })) || []
    );
  }, [optionsData]);

  // Fetch hotel list with filters (uses effectiveCountryCodes to enforce SR destination restriction)
  const { data: hotelsData, isPending: hotelsPending, isError: hotelsError } = useExploreHotelsList(
    debouncedName,
    effectiveCountryCodes,
    bestFors
  );

  const hotels = hotelsData?.data.hotels || [];
  const resultsCount = hotels.length;

  // Loading and error states (include SR destinations loading to prevent flash of unfiltered results)
  const isError = optionsError || hotelsError || destinationsError;
  const filtersLoaded = !optionsPending && !destinationsPending && !isLoadingSrDestinations && destinationOptions.length > 0;
  const showPageLoading = !filtersLoaded;

  const secondaryText = hotelsPending
    ? 'Loading...'
    : resultsCount > 0
    ? `${resultsCount === 1 ? '1 result' : `${resultsCount} results`} found`
    : 'no results found';

  return (
    <>
      <div className="w-auto mx-[20px] crm-desktop:mx-[80px]">
        <PageTitle
          className="flex flex-col gap-1"
          primaryText={
            <>
              <span>Hotels Collection</span> <span className="inline-block m-0 p-0 text-[26px]">({secondaryText})</span>
            </>
          }
        />

        {isError && <ErrorBar />}
        {!isError && showPageLoading && <LoadingBar />}
        {!isError && filtersLoaded && (
          <>
            {/* Mobile: Show Filters button (only visible < md breakpoint) */}
            <div className="block md:hidden mt-[20px]">
              <FluidButton type="secondary" onClick={() => setShowFiltersDrawer(true)}>
                Show Filters
              </FluidButton>
            </div>

            <div
              className={`grid
grid-cols-1
md:grid-cols-[300px_1fr]
gap-[40px]
mt-[30px]`}
            >
              {/* Left Sidebar - Filters */}
              <div className="sticky top-[160px] self-start w-[300px] hidden md:flex flex-col gap-[20px]">
                <span className="block font-pt-sans font-bold text-[17px] text-black">Filter Results</span>

                {/* Search Input */}
                <TextInput
                  id="hotel-search"
                  value={name}
                  onChange={e => setName(e.currentTarget.value)}
                  placeholder="Search Hotel ..."
                  prefixIcon={
                    <SvgIcon IconComponent={MagnifyingGlassIcon} className="w-[20px] h-[20px] fill-light-gray" />
                  }
                />

                {/* Destination Filter */}
                {filteredDestinationOptions.length > 0 && (
                  <FilterChipGroup
                    label="DESTINATION"
                    options={filteredDestinationOptions}
                    selectedValues={countryCodes}
                    onChange={setCountryCodes}
                  />
                )}

                {/* Best For Filter */}
                <FilterChipGroup
                  label="BEST FOR"
                  options={bestForOptions}
                  selectedValues={bestFors}
                  onChange={setBestFors}
                />
              </div>

              {/* Right Content Area - Results Grid */}
              <div>
                {hotelsPending ? (
                  <LoadingBar label="Loading hotels..." />
                ) : hotels.length === 0 ? (
                  <div className="text-left text-black font-pt-sans italic text-[15px] -mt-[0] md:mt-[40px]">
                    No Results Found.
                  </div>
                ) : (
                  // this is the grid of just the hotel cards
                  <div
                    className={`grid
grid-cols-2
lg:grid-cols-3
xl:grid-cols-4
crm-desktop-hd:grid-cols-5
gap-[20px]`}
                  >
                    {hotels.map(hotel => {
                      const formattedStarRating = formatStarRating(hotel.starRating);
                      const hotelLocation = formatLocation(hotel.region, hotel.countryName);

                      return (
                        <Link
                          key={hotel.uuid}
                          to={{
                            pathname: `/hotels-collection/${hotel.uuid}`,
                            // Use window.location to get current URL since replaceState doesn't update React Router's location
                            state: { fromList: `${window.location.pathname}${window.location.search}` },
                          }}
                          className="bg-gray-10 flex flex-col no-underline hover:shadow-pe4 transition-shadow"
                        >
                          {/* Hotel Image */}
                          <div className="relative w-full h-[180px]">
                            {hotel.imageUrl ? (
                              <img src={hotel.imageUrl} alt={hotel.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gray-20" />
                            )}

                            {/* Preferred Label Overlay */}
                            {hotel.preferred === true && (
                              <div className="absolute top-[15px] left-[15px] bg-brown-prime px-[10px] py-[5px]">
                                <span className="font-hurmegeometric-sans font-semibold text-[13px] text-white uppercase tracking-wider">
                                  Preferred
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Card Content */}
                          <div className="p-[15px] flex flex-col gap-[15px]">
                            {/* Star Rating */}
                            {formattedStarRating && (
                              <div className="flex items-center gap-[5px]">
                                <SvgIcon IconComponent={StarIcon} className="w-[18px] h-[18px] fill-brown-prime" />
                                <span className="font-hurmegeometric-sans text-[12px] text-brown-prime uppercase">
                                  {formattedStarRating}
                                </span>
                              </div>
                            )}

                            {/* Hotel Name */}
                            <span className="block font-noe-display text-[19px] leading-[1.276] tracking-wider text-brown-prime">
                              {hotel.name}
                            </span>

                            {/* Location */}
                            <span className="block font-hurmegeometric-sans text-[14px] text-gray-100 uppercase">
                              {hotelLocation}
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <FiltersDrawer
        isOpen={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        onApply={applyFilters}
        currentFilters={currentFilters}
        destinationOptions={filteredDestinationOptions}
        bestForOptions={bestForOptions}
      />
    </>
  );
};
